import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/cn';
import { routes } from '../../routes/routes';

import { ReactComponent as WordmarkArt } from './svg/wordmark.svg';
import { ReactComponent as MarkArt } from './svg/mark.svg';

import './brand.css';

/**
 * The Sleevr brand lockup.
 *
 * Until now every shell typed the wordmark out: `Sleevr` plus a `<span>` for
 * the full stop, styled in the display face. That reads well and is what the
 * templates do, but it is not the logo. It is a typographic impression of one,
 * it drifts the moment the brand face changes, and the real mark, the S, was
 * missing from the product entirely.
 *
 * WHERE THE ART CAME FROM
 * -----------------------
 * `public/logo.svg`, the existing lockup in this repo. Two changes, both
 * mechanical:
 *
 * 1. `fill="#F5F5F5"` became `currentColor` on all seven paths. The source is
 *    near-white, drawn for a dark background, so as shipped it was invisible in
 *    light theme. Inheriting means one asset is correct in both themes and the
 *    logo is ink, exactly like the rest of the system.
 * 2. Two Figma inner-shadow filters removed. At the 20 to 32px a shell renders
 *    this at, an inner bevel is invisible, and each filter carried its own
 *    hardcoded colour that would not have followed the theme.
 *
 * `mark.svg` is the LOCKUP'S OWN FIRST PATH with a cropped viewBox, not a
 * redrawn copy, so the two cannot drift apart.
 *
 * The repo also holds `SleevrLogoDark.png` and `SleevrLogoLight.png`, a raster
 * pair of the same lockup. They are not used here: two files that must be kept
 * in sync, at one fixed resolution, to solve a problem `currentColor` solves
 * for free.
 *
 * WHICH VARIANT
 * -------------
 * `lockup` is the default and is right almost everywhere: top nav, auth, status
 * pages, the console sidebar. `mark` is for genuinely tight space, a collapsed
 * sidebar or a mobile bar with a back button and actions competing for the row.
 * Reach for `mark` because the space is tight, never because it looks neater.
 */

const SIZES = { sm: 'dsa-brand--sm', md: null, lg: 'dsa-brand--lg' };

/* A modified click opens home in a NEW tab or window and leaves this one where
   it is, so it must not move the page the reader kept. `Link` makes the same
   check before it decides to route rather than let the browser follow the
   href; this is the same test, for the same reason. */
function isModifiedClick(event) {
  return (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

/* HOME MEANS THE TOP OF HOME, and the scroll is the half of it that a `<Link>`
   cannot do on its own.
   `/` and `/marketplace` mount the same `<Marketplace />`, so the click that
   used to go from one to the other changed the URL and nothing else: React
   keeps the component mounted, and this app has no scroll restoration, so a
   buyer scrolled halfway down the browse grid saw the logo do literally
   nothing. Going to `/` fixes the destination; this fixes what a reader
   actually judges the click by, and it is right on the surfaces where the
   destination IS the current page too — the front door and the marketplace,
   which on mobile are two of the four places the lockup appears at all. */
function handleClick(event) {
  if (isModifiedClick(event)) return;
  window.scrollTo({ top: 0 });
}

export function Wordmark({
  variant = 'lockup',
  size = 'md',
  to = routes.HOME,
  label = 'Sleevr, home',
  className,
  ...rest
}) {
  const Art = variant === 'mark' ? MarkArt : WordmarkArt;

  const art = (
    <span className={cn('dsa-brand', `dsa-brand--${variant}`, SIZES[size], className)} {...rest}>
      <Art />
    </span>
  );

  // A logo that goes home is a link and must be one, so it is keyboard
  // reachable and announces where it goes. `to={null}` renders the art alone,
  // for the places already inside a link or where home is the current page.
  if (!to) return art;

  return (
    <Link className="dsa-brand__link" to={to} aria-label={label} onClick={handleClick}>
      {art}
    </Link>
  );
}

export default Wordmark;
